// src/hooks/useDoubleTap.ts
import { useCallback, useRef } from "react";
function useDoubleTap({ onDoubleTap, delay = 300 }) {
  const lastTapRef = useRef(0);
  const lastTargetRef = useRef(null);
  const onPointerDown = useCallback(
    (event) => {
      const now = Date.now();
      const target = event.target;
      if (now - lastTapRef.current < delay && lastTargetRef.current === target) {
        event.preventDefault();
        onDoubleTap(event);
        lastTapRef.current = 0;
        lastTargetRef.current = null;
      } else {
        lastTapRef.current = now;
        lastTargetRef.current = target;
      }
    },
    [onDoubleTap, delay]
  );
  return { onPointerDown };
}

// src/hooks/useHashRouter.ts
import { useCallback as useCallback2, useEffect, useState } from "react";
function useHashRouter({ parse, serialize, initial }) {
  const [state, setState] = useState(() => {
    if (typeof window === "undefined") return initial;
    return parse(window.location.hash);
  });
  useEffect(() => {
    const handler = () => setState(parse(window.location.hash));
    window.addEventListener("hashchange", handler);
    return () => window.removeEventListener("hashchange", handler);
  }, [parse]);
  const navigate = useCallback2(
    (next) => {
      window.location.hash = serialize(next);
    },
    [serialize]
  );
  const replace = useCallback2(
    (next) => {
      window.history.replaceState(null, "", `#${serialize(next)}`);
      setState(next);
    },
    [serialize]
  );
  return { state, navigate, replace };
}

// src/hooks/useHierarchy.ts
import {
  ItemLimitExceededError,
  archiveNode as sharedArchiveNode,
  collectArchivedFolders as sharedCollectArchivedFolders,
  countFolders as sharedCountFolders,
  deleteNode as sharedDeleteNode,
  emptyTrash as sharedEmptyTrash,
  expandAncestors as sharedExpandAncestors,
  findById as sharedFindById,
  generateUniqueName as sharedGenerateUniqueName,
  getFolderDisplayPath as sharedGetFolderDisplayPath,
  getFolderPath as sharedGetFolderPath,
  moveNode as sharedMoveNode,
  moveNodeToIndex as sharedMoveNodeToIndex,
  nameExists as sharedNameExists,
  renameNode as sharedRenameNode,
  reorderNode as sharedReorderNode,
  setExpanded as sharedSetExpanded,
  toggleExpanded as sharedToggleExpanded,
  unarchiveNode as sharedUnarchiveNode,
  toArray
} from "@jbr-jazz/hierarchy-shared";
import { useCallback as useCallback3, useMemo } from "react";
import {
  CircularReferenceError as CircularReferenceError2,
  ItemLimitExceededError as ItemLimitExceededError2,
  NamingConflictError as NamingConflictError2
} from "@jbr-jazz/hierarchy-shared";
function useHierarchy(options) {
  const { root, owner, createNode, countNode, limits, checkCanCreate } = options;
  const isLoading = root === void 0;
  const allFolders = useMemo(() => {
    if (!root?.folders) return [];
    return toArray(root.folders).filter((f) => f != null);
  }, [root?.folders]);
  const folders = useMemo(() => {
    return allFolders.filter((f) => !f.archived);
  }, [allFolders]);
  const archivedFolders = useMemo(() => {
    if (!root) return [];
    return sharedCollectArchivedFolders(root);
  }, [root]);
  const countPredicate = useMemo(() => {
    return countNode ?? (() => true);
  }, [countNode]);
  const itemCount = useMemo(() => {
    if (!root) return 0;
    return sharedCountFolders(root, countPredicate);
  }, [root, countPredicate]);
  const canCreate = useMemo(() => {
    if (!checkCanCreate || limits === void 0) return true;
    return checkCanCreate(limits, itemCount);
  }, [checkCanCreate, limits, itemCount]);
  const nameExists = useCallback3(
    (name, parent) => {
      if (!root) return false;
      return sharedNameExists(root, name, parent);
    },
    [root]
  );
  const generateUniqueName = useCallback3(
    (baseName, parent) => {
      if (!root) return baseName;
      return sharedGenerateUniqueName(root, baseName, parent);
    },
    [root]
  );
  const findById = useCallback3(
    (folderId) => {
      if (!root) return null;
      return sharedFindById(root, folderId);
    },
    [root]
  );
  const deleteNode = useCallback3(
    (node) => {
      if (!root) return;
      sharedDeleteNode(root, node);
    },
    [root]
  );
  const moveNodeToIndex = useCallback3(
    (node, newParent, index) => {
      if (!root) return;
      sharedMoveNodeToIndex(root, node, newParent, index);
    },
    [root]
  );
  const moveNode = useCallback3(
    (node, newParent) => {
      if (!root) return;
      sharedMoveNode(root, node, newParent);
    },
    [root]
  );
  const reorderNode = useCallback3(
    (node, newIndex) => {
      if (!root) return;
      sharedReorderNode(root, node, newIndex);
    },
    [root]
  );
  const emptyTrash = useCallback3(() => {
    if (!root) return 0;
    return sharedEmptyTrash(root);
  }, [root]);
  const addFolder = useMemo(() => {
    if (!createNode) return void 0;
    return (name, parent, checkLimit = true) => {
      if (!root?.folders) return null;
      if (checkLimit && !canCreate) {
        throw new ItemLimitExceededError();
      }
      const newFolder = createNode(name, owner, parent);
      if (parent?.children) {
        const list = parent.children;
        list.$jazz.push(newFolder);
      } else {
        const list = root.folders;
        list.$jazz.push(newFolder);
      }
      return newFolder;
    };
  }, [root, owner, canCreate, createNode]);
  const duplicateNode = useMemo(() => {
    if (!createNode) return void 0;
    return (node) => {
      if (!root?.folders) return null;
      const baseName = `${node.name} (Copy)`;
      const uniqueName = generateUniqueName(baseName, node.parent);
      const duplicate = createNode(uniqueName, owner, node.parent);
      duplicate.$jazz?.set("expanded", false);
      const parent = node.parent;
      if (parent?.children) {
        const list = parent.children;
        list.$jazz.push(duplicate);
      } else {
        const list = root.folders;
        list.$jazz.push(duplicate);
      }
      return duplicate;
    };
  }, [root, owner, generateUniqueName, createNode]);
  return useMemo(
    () => ({
      // Computed
      folders,
      allFolders,
      archivedFolders,
      itemCount,
      canCreate,
      limits,
      isLoading,
      // Root-bound operations
      addFolder,
      deleteNode,
      moveNode,
      moveNodeToIndex,
      reorderNode,
      emptyTrash,
      findById,
      nameExists,
      generateUniqueName,
      duplicateNode,
      // Stateless operations - reference shared functions directly
      // These don't need useCallback since the imported functions are stable
      renameNode: sharedRenameNode,
      toggleExpanded: sharedToggleExpanded,
      setExpanded: sharedSetExpanded,
      expandAncestors: sharedExpandAncestors,
      archiveNode: sharedArchiveNode,
      unarchiveNode: sharedUnarchiveNode,
      getFolderPath: sharedGetFolderPath,
      getFolderDisplayPath: sharedGetFolderDisplayPath
    }),
    [
      folders,
      allFolders,
      archivedFolders,
      itemCount,
      canCreate,
      limits,
      isLoading,
      addFolder,
      deleteNode,
      moveNode,
      moveNodeToIndex,
      reorderNode,
      emptyTrash,
      findById,
      nameExists,
      generateUniqueName,
      duplicateNode
    ]
  );
}

// src/hooks/usePWAInstall.ts
import { useCallback as useCallback4, useEffect as useEffect2, useState as useState2 } from "react";
function usePWAInstall() {
  const [promptEvent, setPromptEvent] = useState2(null);
  const [isStandalone, setIsStandalone] = useState2(false);
  useEffect2(() => {
    if (typeof window === "undefined") return;
    setIsStandalone(
      window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true
    );
    const handler = (e) => {
      e.preventDefault();
      setPromptEvent(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);
  const install = useCallback4(async () => {
    if (!promptEvent) return;
    await promptEvent.prompt();
    await promptEvent.userChoice;
    setPromptEvent(null);
  }, [promptEvent]);
  return { isInstallable: promptEvent !== null, isStandalone, install };
}

// src/hooks/useSharing.ts
import { Account } from "jazz-tools";
import { useCallback as useCallback6, useMemo as useMemo2 } from "react";

// src/lib/api-utils.ts
import { useCallback as useCallback5, useRef as useRef2, useState as useState3 } from "react";
function useApiCall(options) {
  const { apiBaseUrl, getAuthHeaders } = options;
  const [isLoading, setIsLoading] = useState3(false);
  const [error, setError] = useState3(null);
  const activeCallsRef = useRef2(0);
  const clearError = useCallback5(() => {
    setError(null);
  }, []);
  const call = useCallback5(
    async (requestOptions) => {
      const { path, method, body, requireAuth = true, transform, defaultOnError } = requestOptions;
      activeCallsRef.current++;
      setIsLoading(true);
      setError(null);
      try {
        const headers = {};
        if (requireAuth) {
          const authHeaders = await getAuthHeaders();
          Object.assign(headers, authHeaders);
        }
        if (body !== void 0) {
          headers["Content-Type"] = "application/json";
        }
        const response = await fetch(`${apiBaseUrl}${path}`, {
          method,
          headers,
          credentials: "include",
          body: body !== void 0 ? JSON.stringify(body) : void 0
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || data.error || `Request failed: ${response.status}`);
        }
        return transform ? transform(data) : data;
      } catch (err) {
        const apiError = err instanceof Error ? err : new Error(String(err));
        setError(apiError);
        if (defaultOnError !== void 0) {
          return defaultOnError;
        }
        throw apiError;
      } finally {
        activeCallsRef.current--;
        if (activeCallsRef.current === 0) {
          setIsLoading(false);
        }
      }
    },
    [apiBaseUrl, getAuthHeaders]
  );
  return {
    state: { isLoading, error, clearError },
    call
  };
}
function normalizeError(err) {
  return err instanceof Error ? err : new Error(String(err));
}

// src/hooks/useSharing.ts
function useSharing(options) {
  const { call, state } = useApiCall(options);
  const createInvite = useCallback6(
    async (targetId, recipientEmail, permission, appRole, sendEmail, expiresInDays) => {
      return call({
        path: "/api/shares/invite",
        method: "POST",
        body: {
          targetId,
          recipientEmail,
          permission,
          ...appRole ? { appRole } : {},
          ...sendEmail === void 0 ? {} : { sendEmail },
          ...expiresInDays === void 0 ? {} : { expiresInDays }
        }
      });
    },
    [call]
  );
  const createInviteAndGrantAgent = useCallback6(
    async (target, recipientEmail, permission, appRole, sendEmail, expiresInDays) => {
      const result = await createInvite(
        target.$jazz.id,
        recipientEmail,
        permission,
        appRole,
        sendEmail,
        expiresInDays
      );
      if (result.agentAccountId) {
        const group = target.$jazz.owner;
        if (!group) {
          throw new Error(
            "Invite target is not owned by a group; cannot grant the sharing agent access."
          );
        }
        const alreadyMember = group.members.some(
          (m) => m.id === result.agentAccountId
        );
        if (!alreadyMember) {
          const agentAccount = await Account.load(result.agentAccountId, {
            loadAs: group.$jazz.loadedAs
          });
          if (!agentAccount) {
            throw new Error("Could not load sharing agent. Please try again.");
          }
          group.addMember(agentAccount, "admin");
          await group.$jazz.waitForSync();
        }
      }
      return result;
    },
    [createInvite]
  );
  const validateInvite = useCallback6(
    async (token) => {
      return call({
        path: `/api/shares/validate/${token}`,
        method: "GET",
        requireAuth: false,
        defaultOnError: { valid: false, error: "not_found" }
      });
    },
    [call]
  );
  const acceptInvite = useCallback6(
    async (token) => {
      return call({
        path: "/api/shares/accept",
        method: "POST",
        body: { token },
        defaultOnError: null
      });
    },
    [call]
  );
  const getPendingInvites = useCallback6(
    async (targetId) => {
      return call({
        path: `/api/shares/targets/${encodeURIComponent(targetId)}/invites`,
        method: "GET",
        transform: (data) => data.invites || [],
        defaultOnError: []
      });
    },
    [call]
  );
  const revokeInvite = useCallback6(
    async (token) => {
      try {
        await call({
          path: `/api/shares/invites/${token}`,
          method: "DELETE"
        });
        return true;
      } catch {
        return false;
      }
    },
    [call]
  );
  const getCollaborators = useCallback6(
    async (targetId) => {
      return call({
        path: `/api/shares/targets/${encodeURIComponent(targetId)}/collaborators`,
        method: "GET",
        transform: (data) => data.collaborators || [],
        defaultOnError: []
      });
    },
    [call]
  );
  const removeCollaborator = useCallback6(
    async (targetId, accountId) => {
      try {
        await call({
          path: `/api/shares/targets/${encodeURIComponent(targetId)}/collaborators/${encodeURIComponent(accountId)}`,
          method: "DELETE"
        });
        return true;
      } catch {
        return false;
      }
    },
    [call]
  );
  const getUserMemberships = useCallback6(async () => {
    const result = await call({
      path: "/api/shares/user/memberships",
      method: "GET",
      defaultOnError: { memberships: [] }
    });
    return result.memberships;
  }, [call]);
  return useMemo2(
    () => ({
      createInvite,
      createInviteAndGrantAgent,
      validateInvite,
      acceptInvite,
      getPendingInvites,
      revokeInvite,
      getCollaborators,
      removeCollaborator,
      getUserMemberships,
      isLoading: state.isLoading,
      error: state.error
    }),
    [
      createInvite,
      createInviteAndGrantAgent,
      validateInvite,
      acceptInvite,
      getPendingInvites,
      revokeInvite,
      getCollaborators,
      removeCollaborator,
      getUserMemberships,
      state.isLoading,
      state.error
    ]
  );
}

// src/hooks/useSubscription.ts
import {
  useSubscription
} from "@jbr-jazz/billing-client";

// src/hooks/useTheme.ts
import { useEffect as useEffect3, useState as useState4 } from "react";
function resolveTheme(t) {
  if (t !== "system") return t;
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
function useTheme({
  storageKey = "jbr-jazz-theme",
  defaultTheme = "system"
} = {}) {
  const [theme, setThemeState] = useState4(() => {
    if (typeof window === "undefined") return defaultTheme;
    return localStorage.getItem(storageKey) || defaultTheme;
  });
  const [resolved, setResolved] = useState4(() => resolveTheme(theme));
  useEffect3(() => {
    const resolvedTheme = resolveTheme(theme);
    setResolved(resolvedTheme);
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(resolvedTheme);
    localStorage.setItem(storageKey, theme);
  }, [theme, storageKey]);
  useEffect3(() => {
    if (theme !== "system") return;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => setResolved(resolveTheme("system"));
    media.addEventListener("change", handler);
    return () => media.removeEventListener("change", handler);
  }, [theme]);
  const setTheme = (t) => setThemeState(t);
  return { theme, resolved, setTheme };
}

// src/hooks/useTreeState.ts
import { useCallback as useCallback7, useMemo as useMemo3, useState as useState5 } from "react";
function getAllFolderIds(folders) {
  const ids = [];
  for (const folder of folders) {
    if (!folder) continue;
    const id = folder.$jazz?.id;
    if (id) ids.push(id);
    if (folder.children) {
      const children = folder.children;
      ids.push(...getAllFolderIds([...children]));
    }
  }
  return ids;
}
function getAncestorIds(folder) {
  const ids = [];
  let current = folder.parent;
  while (current) {
    const id = current.$jazz?.id;
    if (id) ids.push(id);
    current = current.parent;
  }
  return ids;
}
function useTreeState(options = {}) {
  const { viewState, defaultExpanded = false, initialExpanded = [] } = options;
  const [localExpanded, setLocalExpanded] = useState5(() => {
    const initial = {};
    for (const id of initialExpanded) {
      initial[id] = true;
    }
    return initial;
  });
  const isPersisted = viewState != null;
  const getExpandedRecord = useCallback7(() => {
    if (viewState?.folderExpanded) {
      return viewState.folderExpanded;
    }
    return localExpanded;
  }, [viewState, localExpanded]);
  const isExpanded = useCallback7(
    (folderId) => {
      const record = getExpandedRecord();
      const value = record[folderId];
      return value ?? defaultExpanded;
    },
    [getExpandedRecord, defaultExpanded]
  );
  const setExpanded = useCallback7(
    (folderId, expanded) => {
      if (viewState) {
        const folderExpanded = viewState.folderExpanded;
        if (folderExpanded) {
          folderExpanded[folderId] = expanded;
        }
      } else {
        setLocalExpanded((prev) => ({
          ...prev,
          [folderId]: expanded
        }));
      }
    },
    [viewState]
  );
  const toggle = useCallback7(
    (folderId) => {
      const current = isExpanded(folderId);
      setExpanded(folderId, !current);
    },
    [isExpanded, setExpanded]
  );
  const expandMany = useCallback7(
    (folderIds) => {
      for (const id of folderIds) {
        setExpanded(id, true);
      }
    },
    [setExpanded]
  );
  const collapseMany = useCallback7(
    (folderIds) => {
      for (const id of folderIds) {
        setExpanded(id, false);
      }
    },
    [setExpanded]
  );
  const expandAncestors = useCallback7(
    (folder) => {
      const ancestorIds = getAncestorIds(folder);
      expandMany(ancestorIds);
    },
    [expandMany]
  );
  const collapseAll = useCallback7(() => {
    if (viewState) {
      const folderExpanded = viewState.folderExpanded;
      if (folderExpanded) {
        for (const key of Object.keys(folderExpanded)) {
          folderExpanded[key] = false;
        }
      }
    } else {
      setLocalExpanded({});
    }
  }, [viewState]);
  const expandAll = useCallback7(
    (folders) => {
      const allIds = getAllFolderIds(folders);
      expandMany(allIds);
    },
    [expandMany]
  );
  const expandedIds = useMemo3(() => {
    const record = getExpandedRecord();
    return Object.entries(record).filter(([, expanded]) => expanded).map(([id]) => id);
  }, [getExpandedRecord]);
  return {
    isExpanded,
    toggle,
    setExpanded,
    expandMany,
    collapseMany,
    expandAncestors,
    collapseAll,
    expandAll,
    expandedIds,
    isPersisted
  };
}

export {
  useDoubleTap,
  useHashRouter,
  useHierarchy,
  CircularReferenceError2 as CircularReferenceError,
  ItemLimitExceededError2 as ItemLimitExceededError,
  usePWAInstall,
  useApiCall,
  normalizeError,
  useSharing,
  useSubscription,
  useTheme,
  useTreeState
};
